import {Component, effect, inject, OnDestroy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {MatCardModule} from '@angular/material/card';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TextInputComponent} from '../../components/form-fields/text-input/text-input.component';
import {
  PasswordInputComponent
} from '../../components/form-fields/password-input/password-input.component';
import {OtpInputComponent} from '../../components/form-fields/otp-input/otp-input.component';
import {Store} from '@ngrx/store';
import {CommonService} from '../../services/common.service';
import {isResendOtpLoading, isSignupLoading, isVerifyEmailLoading} from '../../store/auth.selectors';
import {combineLatest, interval, Subscription} from 'rxjs';
import {map, takeWhile} from 'rxjs/operators';
import {resendOtp, signup, verifyEmail} from '../../store/auth.actions';
import {
  PrimaryButtonComponent
} from "../../components/buttons/primary-button/primary-button.component";
import {emailRegex} from '../../constants/validation.constants';
import {ActivatedRoute, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  SecondaryButtonComponent
} from '../../components/buttons/secondary-button/secondary-button.component';

@Component({
  selector: 'app-signup-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatTooltipModule,
    TextInputComponent,
    PasswordInputComponent,
    OtpInputComponent,
    PrimaryButtonComponent,
    SecondaryButtonComponent
  ],
  templateUrl: './signup-form.component.html',
  styleUrl: './signup-form.component.css'
})
export class SignupFormComponent implements OnDestroy {
  private store = inject(Store);
  private commonService = inject(CommonService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  form: FormGroup;
  otpForm: FormGroup;

  isSignupLoading$ = this.store.select(isSignupLoading);
  isVerifyEmailLoading$ = this.store.select(isVerifyEmailLoading);
  isResendOtpLoading$ = this.store.select(isResendOtpLoading);
  isLoading$ = combineLatest([this.isSignupLoading$, this.isVerifyEmailLoading$]).pipe(
    map(([isSignupLoading, isVerifyEmailLoading]) => isSignupLoading || isVerifyEmailLoading)
  );

  signupSuccess = this.commonService.getSignupSuccess();
  verifyEmailSuccess = this.commonService.getVerifyEmailSuccess();

  // Countdown timer properties
  countdownTimer = 0;
  private timerSubscription?: Subscription;  

  errorMessages = {
    email: '',
    password: '',
    confirmPassword: '',
    otp: ''
  }

  errorMessageMap = {
    email: {
      required: 'Email is required.',
      pattern: 'Please enter a valid email address.'
    },
    password: {
      required: 'Password is required.',
      minlength: 'Password must be at least 6 characters.',
      maxlength: 'Password cannot exceed 20 characters.',
      lowerCaseCheckFailed: 'Include at least 1 lowercase letter.',
      upperCaseCheckFailed: 'Include at least 1 uppercase letter.',
      numberCheckFailed: 'Include at least 1 number.',
      specialCharCheckFailed: 'Include at least 1 special character (e.g., !@#$%).',
      containsSpaceCheckFailed: 'Password cannot contain spaces.'
    },
    confirmPassword: {
      required: 'Please re-enter your password.',
      passwordMatchFailed: 'Please ensure both passwords match'
    },
    otp: {
      required: 'OTP is required.',
      minlength: 'OTP must be 6 digits.',
      maxlength: 'OTP must be 6 digits.',
      pattern: 'OTP must contain numbers only.'
    }
  }

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(emailRegex)]],
      password: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(20),
        this.commonService.validatePassword
      ],
      ],
      confirmPassword: ['', [
        Validators.required,
      ]
      ]
    }, {
      validators: [this.validateConfirmPassword]
    });

    this.otpForm = this.fb.group({
      otp: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
        Validators.pattern('^[0-9]*$')
      ]]
    });

    // Effect to handle when signup is successful
    effect(() => {
      if (this.signupSuccess()) {
        // Disable the email field once OTP form is shown
        this.form.get('email')?.disable();
        // Start the countdown timer
        this.startCountdownTimer();
      }

      if (this.verifyEmailSuccess()) {
        this.form.get('email')?.enable();
      }
    });

    this.route.queryParams
      .pipe(takeUntilDestroyed())
      .subscribe(params => {
        let email = params['emailid'];

        if (email) {
          email = atob(email);

          this.form.patchValue({email});
          this.form.controls['email'].disable();
        }
      })
  }

  ngOnInit(): void {
    this.commonService.removeItemFromLocalStorage('accesshub/verify-email-token');
    this.commonService.setSignupSuccess(false);
  }

  validateConfirmPassword(control: AbstractControl): ValidationErrors | null {
    const passwordControl = control.get('password');
    const confirmPasswordControl = control.get('confirmPassword');

    if (passwordControl && confirmPasswordControl && passwordControl.value !== confirmPasswordControl.value) {
      confirmPasswordControl.setErrors({passwordMatchFailed: true});
    } else if (confirmPasswordControl?.hasError('passwordMatchFailed')) {
      confirmPasswordControl.setErrors(null);
    }

    return null;
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  checkForOtpErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.otpForm, this.errorMessageMap, currentField))
    };
  }

  getResendButtonText(): string {
    if (this.countdownTimer > 0) {
      return `RESEND VERIFICATION CODE (${this.countdownTimer}s)`;
    }
    return 'RESEND VERIFICATION CODE';
  }

  submit() {
    if (this.signupSuccess()) {
      this.submitOtp();
    } else {
      this.submitSignup();
    }
  }

  submitSignup() {
    if (!this.form.valid) {
      this.checkForErrors();
      return;
    }

    const {email, password, confirmPassword} = this.form.getRawValue();
    this.store.dispatch(signup({email, password, confirmPassword}));
  }

  submitOtp() {
    if (!this.otpForm.valid) {
      this.checkForOtpErrors();
      return;
    }

    const otp = this.otpForm.get('otp')?.value;
    this.store.dispatch(verifyEmail({otp}));
  }

  resendOtp() {
    this.store.dispatch(resendOtp({payload: {email: this.form.controls['email'].value}}));
    // Restart the countdown timer after resend
    this.startCountdownTimer();
  }

  startOver() {
    // Stop the countdown timer
    this.stopCountdownTimer();
    
    // Reset countdown timer
    this.countdownTimer = 0;
    
    // Reset forms to initial state
    this.form.reset();
    this.otpForm.reset();
    
    // Clear email field specifically
    this.form.patchValue({ email: '' });
    
    // Clear error messages
    this.errorMessages = {
      email: '',
      password: '',
      confirmPassword: '',
      otp: ''
    };
    
    // Reset signup success state
    this.commonService.setSignupSuccess(false);
    
    // Enable email field
    this.form.get('email')?.enable();
    
    // Logout and navigate
    this.commonService.logout();
    this.router.navigate(['/auth/signup']);
  }

  startCountdownTimer() {
    // Clear any existing timer
    this.stopCountdownTimer();
    
    // Set initial countdown time (30 seconds)
    this.countdownTimer = 30;
    
    // Start the countdown timer
    this.timerSubscription = interval(1000)
      .pipe(
        takeWhile(() => this.countdownTimer > 0)
      )
      .subscribe(() => {
        this.countdownTimer--;
      });
  }

  stopCountdownTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = undefined;
    }
  }

  ngOnDestroy() {
    this.stopCountdownTimer();
    this.commonService.setSignupSuccess(false);
    this.commonService.removeItemFromLocalStorage('accesshub/verify-email-token');
  }

  onDestroy() {
    this.commonService.setSignupSuccess(false);
    this.commonService.removeItemFromLocalStorage('accesshub/verify-email-token');
  }
}
